<template>
  <QaPageShell :title="$t('_QAOverview')" :loading="loading" :error="error" :empty="isEmpty" @retry="load">
    <template #filters>
      <QaFilterBar @change="load" />
    </template>

    <!--
      ชนเพดานต่อคำขอ = ตัวเลขทุกใบข้างล่างคิดจากบางส่วน ห้ามตัดเงียบ
      (KPI ที่ตัดเงียบคือตัวเลขผิดที่ไม่มีใครจับได้ — เพดานจริงอยู่ที่ QaAnalyticsFilter ฝั่ง C#)
    -->
    <v-alert v-if="data.truncated" type="warning" variant="tonal" density="compact" class="mt-3" icon="mdi-alert-outline">
      <span class="text-body-2">ข้อมูลชนเพดานต่อคำขอ (20,000 เคส) — ตัวเลขทั้งหน้านี้คิดจากเท่าที่ดึงมาได้ในช่วง {{ data.windowDays }} วัน ไม่ใช่ทั้งหมด</span>
    </v-alert>

    <!-- KPI -->
    <v-row class="mt-1" dense>
      <v-col v-for="kpi in kpiCards" :key="kpi.key" cols="12" sm="6" md="4" class="qa-kpi-col">
        <v-card class="qa-card pa-4 h-100" variant="flat">
          <div class="d-flex align-start justify-space-between">
            <div class="text-caption text-medium-emphasis">{{ kpi.label }}</div>
            <v-avatar :color="kpi.color" size="30" variant="tonal" rounded="lg">
              <v-icon size="17" :color="kpi.color">{{ kpi.icon }}</v-icon>
            </v-avatar>
          </div>
          <div class="text-h4 font-weight-medium mt-2 qa-kpi-value" :class="kpi.valueClass">{{ kpi.value }}</div>
          <div class="text-caption text-medium-emphasis d-flex align-center flex-wrap ga-1">
            <span>{{ kpi.sub }}</span>
            <QaMockChip v-if="kpi.mock" :text="kpi.mock.text" :reason="kpi.mock.reason" />
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-1" dense>
      <!-- ตารางผลงานเจ้าหน้าที่ -->
      <v-col cols="12" lg="8">
        <v-card class="qa-card" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaAgentPerformance') }}</span>
            <v-spacer />
            <span class="text-caption text-medium-emphasis">{{ $t('qaClickAgentHint') }}</span>
          </v-card-title>
          <v-divider />
          <div class="qa-table-wrap pt-3">
            <v-data-table
              :headers="agentHeaders"
              :items="data.leaderboard"
              :items-per-page="10"
              class="elevation-0 my-data-table qa-table qa-table-mid"
              density="comfortable"
              fixed-header
              hover
              :row-props="{ class: 'qa-row-click' }"
              @click:row="openAgent"
            >
              <!-- ⚠️ บรรทัดรองคือ "บทบาท" (Agent/Chatbot) ไม่ใช่ทีม — ทีมของธนาคารไม่มี
                   แหล่งข้อมูล จึงไม่มีช่อง team ให้อ่านอีกแล้ว (ข้อจำกัดข้อ 1 ของแผน Q10) -->
              <template #[`item.agentName`]="{ item }">
                <QaAgentCell :name="item.agentName" :initials="item.initials" :color="item.color" :sub="item.role" />
              </template>
              <template #[`item.channelMix`]="{ item }">
                <div class="d-flex ga-1">
                  <v-chip size="x-small" color="primary" variant="tonal" label>{{ item.voiceCount }}V</v-chip>
                  <v-chip size="x-small" color="indigo" variant="tonal" label>{{ item.chatCount }}C</v-chip>
                </div>
              </template>
              <!-- ⚠️ avgScore/passRate/trend เป็น null เมื่อยังไม่มีเคสที่ประเมินสำเร็จ —
                   ห้ามแปลงเป็น 0 · "0" บนตารางประเมินผลคนอ่านว่า "ได้ศูนย์คะแนน" -->
              <template #[`item.avgScore`]="{ item }">
                <QaScoreCell :score="item.avgScore" />
              </template>
              <template #[`item.passRate`]="{ item }">
                <QaBar v-if="item.passRate != null" :percent="item.passRate" :value-text="item.passRate + '%'" />
                <span v-else class="text-medium-emphasis">—</span>
              </template>
              <template #[`item.trend`]="{ item }">
                <span v-if="item.trend == null" class="text-medium-emphasis">—</span>
                <span v-else :class="item.trend >= 0 ? 'text-success' : 'text-error'" class="text-body-2 font-weight-medium">
                  <v-icon size="14">{{ item.trend >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                  {{ Math.abs(item.trend) }}
                </span>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-col>

      <!-- เคสที่ต้องตรวจสอบ -->
      <v-col cols="12" lg="4">
        <v-card class="qa-card" variant="flat">
          <v-card-title class="d-flex align-center py-3">
            <span class="text-subtitle-1">{{ $t('qaNeedsReview') }}</span>
            <v-spacer />
            <v-btn variant="text" color="primary" size="small" append-icon="mdi-arrow-right" @click="$router.push({ name: 'QAInvestigations' })">
              {{ $t('qaViewAll') }}
            </v-btn>
          </v-card-title>
          <v-divider />
          <div v-if="!data.needsReview.length" class="pa-8 text-center text-medium-emphasis">
            <v-icon size="34" color="success">mdi-check-circle-outline</v-icon>
            <div class="text-body-2 mt-2">ไม่มีเคสที่ไม่ผ่านในช่วงเวลานี้</div>
          </div>
          <v-list v-else lines="two" density="comfortable">
            <v-list-item v-for="item in data.needsReview" :key="item.caseNo" class="qa-attention" @click="openCase(item.caseNo)">
              <template #prepend>
                <v-avatar :color="item.isGateFail ? 'error' : 'warning'" variant="tonal" size="34" rounded="lg">
                  <v-icon size="18" :color="item.isGateFail ? 'error' : 'warning'">
                    {{ item.isGateFail ? 'mdi-shield-alert-outline' : 'mdi-alert-outline' }}
                  </v-icon>
                </v-avatar>
              </template>
              <v-list-item-title class="text-body-2 font-weight-medium">{{ item.reason }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ item.agentName }} · {{ item.rootIssue }}</v-list-item-subtitle>
              <template #append>
                <div class="text-right">
                  <!-- ⚠️ คะแนนเป็น null ได้ · "—" คือยังไม่มีคะแนน ส่วน 0 คือได้ศูนย์จริง ๆ
                       และไม่ย้อมแดงตอนไม่มีคะแนน เพราะสีแดงคือคำตัดสิน ไม่ใช่ช่องว่าง -->
                  <div class="text-body-2 font-weight-medium" :class="item.totalScore == null ? 'text-medium-emphasis' : 'text-error'">
                    {{ item.totalScore ?? '—' }}
                  </div>
                  <div class="text-caption text-medium-emphasis">{{ formatDateShort(item.evaluatedAt) }}</div>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </QaPageShell>
</template>

<script>
/**
 * หน้าสรุปภาพรวม QA
 *
 * Q10 — ตัวเลขทุกใบมาจาก `analytics/overview` ของจริงแล้ว (เดิมสุ่มจาก mock ทั้งหน้า)
 *
 * ⚠️ KPI ทุกตัวยกเว้น "ยังไม่ประเมิน" คิดจาก**เคสที่ประเมินสำเร็จเท่านั้น** —
 *    เคสที่ถูกข้าม/รอคิว/ประเมินล้ม ไม่ถ่วงค่าเฉลี่ยและไม่นับในตัวส่วนของอัตราผ่าน
 *    จึงต้องมีใบ "ยังไม่ประเมิน" อยู่ข้าง ๆ เสมอ ไม่งั้นคนอ่านจะคิดว่าตัวเลขนี้คือทั้งหมด
 *    (เดิมใบ "เคสที่ประเมินแล้ว" เขียนว่า "ครอบคลุม 100% ของเคสที่เข้าเกณฑ์" ซึ่งไม่จริง)
 */
import qaConsole from '@/services/qaConsole';
import { formatDateShort } from '@/utils/qaFormat';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';
import QaFilterBar from '@/components/QAConsole/QaFilterBar.vue';
import QaAgentCell from '@/components/QAConsole/QaAgentCell.vue';
import QaScoreCell from '@/components/QAConsole/QaScoreCell.vue';
import QaBar from '@/components/QAConsole/QaBar.vue';
import QaMockChip from '@/components/QAConsole/QaMockChip.vue';

export default {
  name: 'QAOverview',
  components: { QaPageShell, QaFilterBar, QaAgentCell, QaScoreCell, QaBar, QaMockChip },
  data() {
    return {
      loading: true,
      error: null,
      data: { kpi: {}, leaderboard: [], needsReview: [], truncated: false, windowDays: 0 },
    };
  },
  computed: {
    isEmpty() {
      // ⚠️ ต้องนับ notEvaluated ด้วย — ฐานที่มีเคสครบแต่ worker ยังไม่ได้ประเมินสักใบ
      //    จะมี totalCases = 0 · ถ้าดูแค่ช่องนั้นหน้าจะขึ้น "ไม่มีข้อมูล" ทั้งที่มีเคสอยู่
      //    และคนดูจะไปไล่หาสาเหตุผิดที่ (ตัวกรอง แทนที่จะเป็นคิวประเมิน)
      const k = this.data.kpi;
      return !this.loading && !this.error && !k.totalCases && !k.notEvaluated;
    },
    agentHeaders() {
      return [
        { title: this.$t('qaAgent'), key: 'agentName', sortable: false, minWidth: '200px' },
        // ⚠️ นับเฉพาะเคสที่ประเมินสำเร็จ — หัวคอลัมน์ต้องบอกให้ตรง ไม่งั้นอ่านเป็นจำนวนเคสทั้งหมด
        { title: this.$t('qaCasesEvaluated'), key: 'caseCount', align: 'end', width: '120px' },
        { title: this.$t('qaNotEvaluatedFilter'), key: 'notEvaluated', align: 'end', width: '110px' },
        { title: this.$t('qaChannelMix'), key: 'channelMix', sortable: false, width: '130px' },
        { title: this.$t('qaAvgScore'), key: 'avgScore', align: 'end', width: '110px' },
        { title: this.$t('qaPassRate'), key: 'passRate', width: '150px' },
        { title: this.$t('qaTrend'), key: 'trend', align: 'end', width: '90px' },
      ];
    },
    kpiCards() {
      const k = this.data.kpi;
      return [
        { key: 'avg', label: this.$t('qaAvgScore'), value: k.avgScore ?? 0, sub: `จาก ${k.totalCases ?? 0} เคสที่ประเมินสำเร็จ`, icon: 'mdi-shield-check', color: 'primary', valueClass: '' },
        { key: 'pass', label: this.$t('qaPassRate'), value: `${k.passRate ?? 0}%`, sub: `ผ่าน ${k.passedCases ?? 0} จาก ${k.totalCases ?? 0}`, icon: 'mdi-check-circle-outline', color: 'success', valueClass: '' },
        { key: 'total', label: this.$t('qaCasesEvaluated'), value: k.totalCases ?? 0, sub: `ยังไม่ประเมินอีก ${k.notEvaluated ?? 0} เคส`, icon: 'mdi-clipboard-check-outline', color: 'indigo', valueClass: '' },
        {
          key: 'review',
          label: this.$t('qaNeedsReview'),
          value: k.needsReview ?? 0,
          sub: `ตก compliance gate ${k.gateFails ?? 0} เคส`,
          icon: 'mdi-alert-outline',
          color: 'error',
          valueClass: 'text-error',
          // ⚠️ เลขนี้เป็น 0 เสมอเพราะยังไม่มีการตรวจ gate จริง ไม่ใช่เพราะไม่มีเคสตก —
          //    ต้องติดป้าย ไม่งั้น "0" จะถูกอ่านเป็นข่าวดีที่ไม่มีอยู่จริง
          mock: { text: 'ยังไม่เปิดการตรวจ', reason: 'compliance gates ยังไม่ถูกตรวจในรอบนี้ ค่านี้จึงเป็น 0 เสมอ' },
        },
        { key: 'notEvaluated', label: this.$t('qaNotEvaluatedFilter'), value: k.notEvaluated ?? 0, sub: 'รวมเคสที่ข้าม รอคิว และประเมินไม่สำเร็จ', icon: 'mdi-progress-clock', color: 'grey', valueClass: '' },
      ];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDateShort,
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await qaConsole.getOverview(this.$store.getters['qaConsole/filter']);
        // แตกทีละช่องแทนการทับทั้งก้อน — ก้อนที่ขาดสักช่อง (list เป็น null) จะทำให้
        // template ล้มทั้งหน้าแทนที่จะแสดงส่วนที่มีอยู่
        this.data = {
          kpi: res.kpi || {},
          leaderboard: res.leaderboard || [],
          needsReview: res.needsReview || [],
          truncated: !!res.truncated,
          windowDays: res.windowDays,
        };
      } catch (err) {
        this.error = err;
      } finally {
        this.loading = false;
      }
    },
    openAgent(_event, { item }) {
      this.$router.push({ name: 'QAAgentDetail', query: { agentId: item.agentId } });
    },
    openCase(caseNo) {
      this.$router.push({ name: 'QACaseDetail', query: { caseNo, from: 'QAOverview' } });
    },
  },
};
</script>

<style scoped>
.qa-attention {
  cursor: pointer;
}
/*
  KPI 5 ใบไม่ลงตัวกับกริด 12 ช่อง (12/5 ไม่เป็นจำนวนเต็ม) — บังคับ 20% เองบนจอกว้าง
  เพื่อให้ได้แถวเดียวเท่ากันทั้ง 5 ใบ · เล็กกว่านั้นปล่อยให้ cols/sm/md จัดตามปกติ
*/
@media (min-width: 1280px) {
  .qa-kpi-col {
    flex: 0 0 20%;
    max-width: 20%;
  }
}
</style>
